<template>
    <nav
        v-if="breadcrumbs.length"
        class="breadcrumbs"
        aria-label="Breadcrumb"
    >
        <ol class="list">
            <li
                v-for="(item, key) in breadcrumbs"
                :key="key"
                class="item"
            >
                <ThLink
                    v-if="item.to && key !== breadcrumbs.length - 1"
                    :to="item.to"
                    class="router-link"
                >
                    {{ item.title }}
                </ThLink>

                <span
                    v-else
                    aria-current="page"
                >
                    {{ item.title }}
                </span>
            </li>
        </ol>
    </nav>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { defineBreadcrumb, useSchemaOrg } from '@unhead/schema-org/vue'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl'
import type { ButtonRouteRelative } from './buttons/useThButton'

import ThLink from '@/components/ui/buttons/ThLink.vue'

const absoluteUrl = useAbsoluteUrl()

export type Breadcrumb = {
    title?: string
    to?: ButtonRouteRelative
}

const props = defineProps<{
    breadcrumbs: Breadcrumb[]
}>()

useSchemaOrg([
    computed(() => {
        if (!props.breadcrumbs.length) {
            return undefined
        }

        return defineBreadcrumb({
            itemListElement: props.breadcrumbs.map((item) => {
                return {
                    name: item.title ?? '',
                    item: item.to ? absoluteUrl(item.to).href : undefined,
                }
            }),
        })
    }),
])
</script>

<style lang="scss" scoped>
.breadcrumbs {
    width: 100%;
    font-family: $effra-font-stack;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.05em;
    font-weight: 400;
    color: $grey-400;

    .list {
        width: 100%;
        display: flex;
        align-items: center;
        overflow-y: auto;

        .item {
            display: block;
            margin: 0 3px 0 0;

            .router-link {
                color: $slate;
            }

            &::after {
                content: '•';
                margin-left: 3px;
                color: $slate;
            }

            &:last-child {
                &::after {
                    display: none;
                }
            }
        }
    }
}
</style>
